import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';
import { compact } from 'lodash';

import { MarkerPin01Icon } from '@/components/icons/MarkerPin01Icon';
import { PaletteIcon } from '@/components/icons/PaletteIcon';
import { User01Icon } from '@/components/icons/User01Icon';
import { Users01Icon } from '@/components/icons/Users01Icon';
import { SearchFilterToolbar } from '@/components/tables/DataTable/components/SearchFilterToolbar';
import {
  AsyncFilterGroup,
  FilterGroup,
  StaticFilterGroup,
} from '@/components/tables/TableFilter/types';
import { STATE_CODE_TO_NAME } from '@/constants/states';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';

import { useClientListPageContext } from '../context/ClientListPage.context';
import {
  RelationshipOwnerOptionsQuery,
  SubBrandOptionsQuery,
  TeamOptionsQuery,
  useRelationshipOwnerOptionsQuery,
  useSubBrandOptionsQuery,
  useTeamOptionsQuery,
} from './graphql/ClientSearchFilterToolbar.generated';

const STATE_FILTER_OPTIONS = Object.entries(STATE_CODE_TO_NAME)
  .map(([value, name]) => ({ value, label: `${name} (${value})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

export function ClientSearchFilterToolbar() {
  const { filterValue, setFilterValue } = useClientListPageContext();
  const { hasSubBrands } = useTenantDetailsContext();

  const subBrandFilterGroup: AsyncFilterGroup<
    SubBrandOptionsQuery,
    OperationVariables
  > = {
    id: 'subBrand',
    label: 'Sub-brand',
    icon: <PaletteIcon size={18} />,
    async: true,
    multi: true,
    useQueryHook: useSubBrandOptionsQuery as unknown as (
      options: QueryHookOptions<SubBrandOptionsQuery, OperationVariables>
    ) => QueryResult<SubBrandOptionsQuery, OperationVariables>,
    searchTermToVariables: (searchTerm) => ({
      search: searchTerm || '',
    }),
    getSelectedOptionVariables: (selectedIds) => ({
      ids: selectedIds.length > 0 ? selectedIds : undefined,
      fetchSelectedData: selectedIds.length > 0,
    }),
    toOptions: (data) => {
      return data
        .map((node) => ({
          value: node.id,
          label: node.displayName,
        }))
        .filter((opt) => opt.value)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  };

  const teamFilterGroup: AsyncFilterGroup<
    TeamOptionsQuery,
    OperationVariables
  > = {
    id: 'team',
    label: 'Team',
    icon: <Users01Icon size={18} />,
    async: true,
    multi: true,
    useQueryHook: useTeamOptionsQuery as unknown as (
      options: QueryHookOptions<TeamOptionsQuery, OperationVariables>
    ) => QueryResult<TeamOptionsQuery, OperationVariables>,
    searchTermToVariables: (searchTerm) => ({
      search: searchTerm || '',
    }),
    getSelectedOptionVariables: (selectedIds) => ({
      ids: selectedIds.length > 0 ? selectedIds : undefined,
      fetchSelectedData: selectedIds.length > 0,
    }),
    toOptions: (data) => {
      return data
        .map((node) => ({
          value: node.id,
          label: node.name,
        }))
        .filter((opt) => opt.value)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  };

  const relationshipOwnerFilterGroup: AsyncFilterGroup<
    RelationshipOwnerOptionsQuery,
    OperationVariables
  > = {
    id: 'relationshipOwner',
    label: 'Relationship owner',
    icon: <User01Icon size={18} />,
    async: true,
    multi: true,
    useQueryHook: useRelationshipOwnerOptionsQuery as unknown as (
      options: QueryHookOptions<
        RelationshipOwnerOptionsQuery,
        OperationVariables
      >
    ) => QueryResult<RelationshipOwnerOptionsQuery, OperationVariables>,
    searchTermToVariables: (searchTerm) => ({
      search: searchTerm || '',
    }),
    getSelectedOptionVariables: (selectedIds) => ({
      ids: selectedIds.length > 0 ? selectedIds : undefined,
      fetchSelectedData: selectedIds.length > 0,
    }),
    toOptions: (data) => {
      return data
        .map((node) => ({
          value: node.user.id,
          label: node.displayName,
        }))
        .filter((opt) => opt.value)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  };

  const stateFilterGroup: StaticFilterGroup = {
    id: 'state',
    label: 'State',
    icon: <MarkerPin01Icon size={18} />,
    multi: true,
    options: STATE_FILTER_OPTIONS,
  };

  const filterGroups: FilterGroup[] = compact([
    hasSubBrands && subBrandFilterGroup,
    relationshipOwnerFilterGroup,
    teamFilterGroup,
    stateFilterGroup,
  ]);

  return (
    <SearchFilterToolbar
      searchProps={{
        placeholder: 'Search clients',
      }}
      tableFilterProps={{
        groups: filterGroups,
        value: filterValue,
        onChange: setFilterValue,
      }}
    />
  );
}
